import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { EnvironmentReport, ToolboxBridge } from "@ttc-spectree/toolbox-contract";
import { toolboxKeys } from "@/lib/toolbox/bridge";

/**
 * Keep the title-bar Claude chip honest (SpecTree Windows walk 1, gap G-04,
 * epic PI-SPEC-SW-E304).
 *
 * The chip reads the environment report (`app.checkEnvironment()`), which is
 * re-checked at most every five minutes. When Claude Code signs out (or back
 * in) between two checks, Help and the runs notice first: Help's status
 * changes, or a Help question, a Formulator turn, a grooming item or the run
 * queue stops with `CLAUDE_NOT_SIGNED_IN`. Each such event re-runs the
 * environment checks at once, so the chip turns amber with "Claude · signed
 * out" (and green again after the sign-in). The FogBugz pages have no event
 * stream: they report each run's result through {@link useClaudeRunOutcome}.
 */

/** What an event says about Claude's sign-in, if anything. */
export type ClaudeSignal = "signed-out" | "signed-in";

const SIGNED_OUT_CODE = "CLAUDE_NOT_SIGNED_IN";

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

/**
 * The sign-in signal in one Help, Formulator or Grooming event, or null.
 * Lenient on purpose (the events are parsed by their own pages): it reads
 * only `type`, `errorCode`, Help's `status.state` and the queue's
 * `pause.errorCode`. Pure; exported for tests.
 */
export function claudeSignalOf(event: unknown): ClaudeSignal | null {
  const e = record(event);
  if (e === null || typeof e.type !== "string") return null;
  switch (e.type) {
    // Help: the engine's readiness changed.
    case "status-changed": {
      const state = record(e.status)?.state;
      if (state === "signed-out") return "signed-out";
      if (state === "ready") return "signed-in";
      return null;
    }
    // Help and Formulator turns, Grooming items and batches.
    case "turn-finished":
    case "item-finished":
    case "batch-stopped":
      return e.errorCode === SIGNED_OUT_CODE ? "signed-out" : null;
    // The run queue paused because the Claude sign-in ran out.
    case "queue-changed": {
      const pause = record(record(e.queue)?.pause);
      return pause?.errorCode === SIGNED_OUT_CODE ? "signed-out" : null;
    }
    default:
      return null;
  }
}

/**
 * True when a signal disagrees with what the chip shows, so the environment
 * checks must run again. With no report yet (still loading) it checks again
 * too: a check started before the sign-out could still say signed in.
 * Pure; exported for tests.
 */
export function chipNeedsRefresh(signal: ClaudeSignal, environment: EnvironmentReport | undefined): boolean {
  if (environment === undefined) return true;
  const signedIn = environment.shared["claude-signed-in"].status !== "fail";
  return signal === "signed-out" ? signedIn : !signedIn;
}

/**
 * Listen to Help, Formulator and Grooming events and re-run the environment
 * checks when one of them shows Claude's sign-in changed (see
 * {@link claudeSignalOf}). Used by the title-bar Claude chip.
 *
 * A signal that arrives while a check is already running may be newer than
 * that check (signed out, re-check starts, signed in again before it ends).
 * It is held and judged against the running check's result once it lands,
 * so a stale signed-out answer cannot stick after the sign-in; only the
 * latest held signal counts.
 */
export function useClaudeChipRefresh(bridge: ToolboxBridge | null, environment: EnvironmentReport | undefined): void {
  const queryClient = useQueryClient();
  const environmentRef = useRef(environment);
  environmentRef.current = environment;
  const heldSignalRef = useRef<ClaudeSignal | null>(null);
  const waitingRef = useRef(false);

  useEffect(() => {
    if (!bridge) return undefined;
    const queryKey = toolboxKeys.environment();
    const recheck = (): void => {
      void queryClient.invalidateQueries({ queryKey });
    };
    const onEvent = (event: unknown): void => {
      const signal = claudeSignalOf(event);
      if (signal === null) return;
      if (queryClient.isFetching({ queryKey }) > 0) {
        heldSignalRef.current = signal;
        if (waitingRef.current) return;
        waitingRef.current = true;
        // cancelRefetch: false joins the running check instead of starting one.
        void queryClient
          .refetchQueries({ queryKey }, { cancelRefetch: false })
          .catch(() => undefined)
          .finally(() => {
            waitingRef.current = false;
            const held = heldSignalRef.current;
            heldSignalRef.current = null;
            if (held !== null && chipNeedsRefresh(held, queryClient.getQueryData<EnvironmentReport>(queryKey))) recheck();
          });
        return;
      }
      if (chipNeedsRefresh(signal, environmentRef.current)) recheck();
    };
    const unsubscribers: (() => void)[] = [];
    for (const toolset of [bridge.help, bridge.formulator, bridge.grooming]) {
      if (typeof toolset?.onEvent !== "function") continue;
      try {
        unsubscribers.push(toolset.onEvent(onEvent));
      } catch {
        // A toolset that cannot be listened to leaves the five-minute re-check.
      }
    }
    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  }, [bridge, queryClient]);
}

/**
 * The sign-in signal in one FogBugz run result (summary, code report or
 * follow-up), or null: `CLAUDE_NOT_SIGNED_IN` is signed out, a finished run
 * is signed in, any other refusal says nothing. Pure; exported for tests.
 */
export function claudeSignalOfRun(result: { ok: true } | { ok: false; errorCode: string }): ClaudeSignal | null {
  if (result.ok) return "signed-in";
  return result.errorCode === SIGNED_OUT_CODE ? "signed-out" : null;
}

/**
 * Report a FogBugz run's result to the title-bar Claude chip (Windows walk
 * gap G-B5, epic PI-SPEC-SW-E307). The fogbugz toolset has no `onEvent`: its
 * runs return `CLAUDE_NOT_SIGNED_IN` in their result, so the FogBugz pages
 * pass each result here, and the environment checks run again when it
 * disagrees with the chip (see {@link chipNeedsRefresh}).
 */
export function useClaudeRunOutcome(): (result: { ok: true } | { ok: false; errorCode: string }) => void {
  const queryClient = useQueryClient();
  return useCallback(
    (result) => {
      const signal = claudeSignalOfRun(result);
      if (signal === null) return;
      const queryKey = toolboxKeys.environment();
      if (chipNeedsRefresh(signal, queryClient.getQueryData<EnvironmentReport>(queryKey))) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
    [queryClient],
  );
}
